import { OverflowList } from '@theme/critical';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, requestYieldCallback } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

let productCardInstanceCounter = 0;

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  /**
   * Product card swatches are rendered as radio buttons. The server-rendered
   * name only includes the product id, which means duplicate cards for the same
   * product (recommendations desktop/mobile lists, duplicate filter cards, etc.)
   * can uncheck each other. Scope radio groups to this card instance so hidden
   * duplicate cards cannot disturb the visible active swatch.
   */
  #scopeCombinedSwatchRadioGroups() {
    let scope =
      this.dataset.productCardId ||
      this.id ||
      this.refs.productCardLink?.id ||
      '';

    if (!scope) {
      productCardInstanceCounter += 1;
      scope = `product-card-${productCardInstanceCounter}`;
    }

    const scopeToken = String(scope)
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-')
      .replace(/^-+|-+$/g, '');

    if (!scopeToken) return;

    this.querySelectorAll('input[type="radio"][name][data-combined-swatch="true"]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;

      if (!input.dataset.originalName) {
        input.dataset.originalName = input.name;
      }

      input.name = `${input.dataset.originalName}--${scopeToken}`;
    });
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#scopeCombinedSwatchRadioGroups();
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);
    this.addEventListener('pointerenter', this.#warmHoverImage);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
      // Warm the metafield hover image as soon as the card mounts so the first
      // hover doesn't race a cold network download.
      this.#warmHoverImage();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    this.removeEventListener('pointerenter', this.#warmHoverImage);
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      // For product cards that use the combined swatches picker,
      // keep the existing picker DOM so the swatch block does not disappear
      const isCombinedSwatchesPicker =
        this.variantPicker instanceof HTMLElement &&
        this.variantPicker.dataset.combinedSwatches === 'true';

      if (!isCombinedSwatchesPicker) {
        this.variantPicker?.updateVariantPicker(event.detail.data.html);
      }
    }

    // Only update variant images if there's no forced glaze, or if the forced glaze variant is being set
    const forcedVariantId = this.#getForcedVariantId();
    if (!forcedVariantId) {
      // No forced glaze - normal behavior
      this.#updateVariantImages();
    } else {
      // Forced glaze is set - only update if the event is setting the forced variant
      const updatedVariantId = event.detail?.resource?.id;
      if (updatedVariantId === forcedVariantId) {
        // This is the forced variant being set - update images
        this.#updateVariantImages();
      }
      // Otherwise, skip to prevent overriding the forced variant
    }
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!slideshow) return;
    
    // PRIORITY 1: Check if we have a forced glaze - if so, ALWAYS use that variant's media ID
    const forcedMediaId = this.#getForcedVariantMediaId();
    if (forcedMediaId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== forcedMediaId;
      }

      slideshow.select({ id: forcedMediaId }, undefined, { animate: false });
      return;
    }

    // PRIORITY 2: Check if card gallery is locked (duplicate being set up)
    const cardGallery = this.refs.cardGallery;
    if (cardGallery?.hasAttribute('data-forced-glaze-locked')) {
      // Don't update images while locked - wait for variant to be set
      return;
    }

    // PRIORITY 3: No forced glaze - use normal behavior
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }

  /**
   * Gets the forced glaze variant ID if this card has a forced glaze.
   * @returns {string | null} The forced variant ID or null if no forced glaze.
   */
  #getForcedVariantId() {
    const parentCard = this.closest('li[data-forced-glaze]');
    if (!parentCard) return null;

    const cardGallery = this.refs.cardGallery;
    if (!cardGallery) return null;

    // Check if we have a stored forced variant ID
    const forcedVariantId = cardGallery.getAttribute('data-forced-variant-id');
    if (forcedVariantId) return forcedVariantId;

    // Fallback: get from data-active-variant-id
    return cardGallery.getAttribute('data-active-variant-id');
  }

  /**
   * Gets the forced variant's media ID if this card has a forced glaze.
   * @returns {string | null} The forced variant media ID or null if no forced glaze.
   */
  #getForcedVariantMediaId() {
    const forcedVariantId = this.#getForcedVariantId();
    if (!forcedVariantId) return null;

    // Find the input with this variant ID - check both checked and unchecked inputs
    // because the picker might not have updated yet
    let variantInput = this.querySelector(`input[data-variant-id="${forcedVariantId}"]`);
    
    // If not found, try to find by the forced glaze name from the parent card
    if (!variantInput) {
      const parentCard = this.closest('li[data-forced-glaze]');
      if (parentCard) {
        const forcedGlaze = parentCard.dataset.forcedGlaze;
        if (forcedGlaze) {
          // Find input that matches the forced glaze
          const allInputs = this.querySelectorAll('input[data-variant-id]');
          for (const input of allInputs) {
            const label = input.closest('label');
            if (label) {
              const labelText = label.textContent || '';
              const title = label.getAttribute('title') || '';
              const ariaLabel = label.getAttribute('aria-label') || '';
              
              // Check if this input's label matches the forced glaze
              if (labelText.includes(forcedGlaze) || 
                  title.includes(forcedGlaze) || 
                  ariaLabel.includes(forcedGlaze)) {
                variantInput = input;
                break;
              }
            }
          }
        }
      }
    }
    
    if (!variantInput) return null;

    return variantInput.dataset.optionMediaId || null;
  }

  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    // Disable variant preview for product cards in recommendations section
    if (this.hasAttribute('data-in-recommendations')) {
      return;
    }

    const { slideshow } = this.refs;
    if (!slideshow) {
      return;
    }

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Commits a product-card swatch selection to the gallery.
   * Unlike previewVariant(), this updates the card's active variant state so
   * hover/reset logic and later image previews do not snap back to the filter
   * variant or the initial card variant.
   * @param {HTMLInputElement} input
   */
  selectCombinedSwatch(input) {
    const variantId = input.dataset.variantId;
    const mediaId = input.dataset.optionMediaId;
    const isForcedGlazeSync = input.dataset.forcedGlazeSync === 'true';

    if (!variantId || !mediaId) return;

    this.resetVariant.cancel();

    const cardGallery = this.refs.cardGallery;
    if (cardGallery instanceof HTMLElement) {
      // Keep the server-rendered hover URL when the carousel/swatch sync is for
      // the same variant the card already shows. Blindly removing it (plus the
      // data-combined-swatch-selected early-return in previewImage) was why Shop
      // All cards with a correct hover_image metafield still had no hover.
      const previousHoverVariantId = cardGallery.getAttribute('data-variant-id-for-hover');
      if (previousHoverVariantId !== variantId) {
        cardGallery.removeAttribute('data-hover-image-url');
      }
      cardGallery.setAttribute('data-active-variant-id', variantId);
      cardGallery.setAttribute('data-variant-id-for-hover', variantId);

      if (isForcedGlazeSync) {
        cardGallery.setAttribute('data-forced-variant-id', variantId);
        cardGallery.removeAttribute('data-combined-swatch-selected');
      } else {
        cardGallery.setAttribute('data-combined-swatch-selected', 'true');
        cardGallery.removeAttribute('data-forced-variant-id');
        cardGallery.removeAttribute('data-forced-glaze-locked');
      }

      // Prefetch the hover image for the newly active variant so the next
      // hover doesn't wait on a cold download / section fetch.
      this.#warmHoverImage();
    }

    const { slideshow } = this.refs;
    if (!slideshow) return;

    this.#preloadSlideImage(mediaId);

    const targetSlide = slideshow.refs.slides?.find((slide) => slide.getAttribute('slide-id') === mediaId);
    const targetImage = targetSlide?.querySelector('img');
    const currentSlide =
      slideshow.refs.slides?.find(
        (slide) => slide.getAttribute('aria-hidden') === 'false' && !slide.hasAttribute('hidden')
      ) ||
      slideshow.slides?.[slideshow.current] ||
      slideshow.refs.slides?.find((slide) => slide.getAttribute('aria-hidden') === 'false');
    const currentImage = currentSlide?.querySelector('img');

    if (targetImage instanceof HTMLImageElement && currentImage instanceof HTMLImageElement) {
      currentImage.loading = 'eager';
      currentImage.fetchPriority = 'high';
      currentImage.alt = targetImage.alt;
      currentImage.sizes = targetImage.sizes;
      currentImage.srcset = targetImage.srcset;
      currentImage.src = targetImage.currentSrc || targetImage.src;
      currentImage.dataset.combinedSwatchMediaId = mediaId;
    }

    // Select by media id while preserving the slideshow's own hidden-slide
    // reveal logic. If we hide/show slides first, the visible slide index can
    // stay at 0 and the slideshow may skip the actual image move.
    slideshow.select({ id: mediaId }, undefined, { animate: false });
  }

  /** @type {Map<string, string>} Cache hover images by variant ID */
  #hoverImageCache = new Map();

  /** @type {Set<string>} Media ids already promoted from lazy loading */
  #preloadedMediaIds = new Set();

  /** @type {Set<string>} Hover image URLs already downloaded into browser cache */
  #preloadedHoverUrls = new Set();

  /** @type {number} Token used to ignore stale async hover fetches */
  #hoverPreviewToken = 0;

  /**
   * Downloads a hover image URL into the browser cache before we swap it onto
   * the visible card image. Without this, the first hover sets src to a cold
   * URL, the browser blanks while loading, and a quick mouse-leave restores
   * the original — so it looks like hover "doesn't work until the second try".
   * @param {string | null | undefined} url
   * @returns {Promise<boolean>}
   */
  #preloadHoverImageUrl(url) {
    if (!url || !url.trim()) return Promise.resolve(false);
    if (this.#preloadedHoverUrls.has(url)) return Promise.resolve(true);

    return new Promise((resolve) => {
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => {
        this.#preloadedHoverUrls.add(url);
        resolve(true);
      };
      img.onerror = () => resolve(false);
      img.src = url;
    });
  }

  /**
   * Resolve + preload the hover image for the card's current variant.
   * Safe to call on mount and again on pointerenter.
   */
  #warmHoverImage = () => {
    const cardGallery = this.refs.cardGallery;
    if (!(cardGallery instanceof HTMLElement)) return;
    if (cardGallery.hasAttribute('data-informative-mode')) return;

    const htmlHoverUrl = cardGallery.getAttribute('data-hover-image-url');
    const currentVariantId = this.#getCurrentVariantId();
    const htmlVariantId = cardGallery.getAttribute('data-variant-id-for-hover');

    if (htmlHoverUrl && htmlHoverUrl.trim() && currentVariantId === htmlVariantId) {
      this.#preloadHoverImageUrl(htmlHoverUrl);
      return;
    }

    if (!currentVariantId) return;

    const cached = this.#hoverImageCache.get(currentVariantId);
    if (cached) {
      this.#preloadHoverImageUrl(cached);
      return;
    }
    if (cached === '') return;

    // Kick off the section fetch early so the first hover rarely waits on it.
    this.#fetchHoverImage().then((url) => {
      if (url) this.#preloadHoverImageUrl(url);
    });
  };

  /**
   * Promotes a slideshow image from lazy loading so a swatch selection can paint
   * immediately instead of waiting for scroll/hover/layout to trigger loading.
   * @param {string} mediaId
   */
  #preloadSlideImage(mediaId) {
    if (this.#preloadedMediaIds.has(mediaId)) return;

    const slide = this.refs.slideshow?.refs.slides?.find((item) => item.getAttribute('slide-id') === mediaId);
    const image = slide?.querySelector('img');

    if (!(image instanceof HTMLImageElement)) return;

    image.loading = 'eager';
    image.fetchPriority = 'high';

    const preload = new Image();
    if (image.sizes) preload.sizes = image.sizes;
    if (image.srcset) preload.srcset = image.srcset;
    preload.src = image.currentSrc || image.src;

    image.decode?.().catch(() => {});
    this.#preloadedMediaIds.add(mediaId);
  }

  /**
   * Preloads the variant media represented by combined swatches in this card.
   * @param {Iterable<HTMLInputElement>} inputs
   */
  preloadCombinedSwatchImages(inputs) {
    for (const input of inputs) {
      const mediaId = input.dataset.optionMediaId;
      if (mediaId) this.#preloadSlideImage(mediaId);
    }
  }

  /**
   * Gets the current variant ID that should be used for hover image
   * @returns {string | null}
   */
  #getCurrentVariantId() {
    const cardGallery = this.refs.cardGallery;
    if (!cardGallery) return null;
    
    // Priority: active-variant-id (set by filters) > forced-variant-id > variant-id-for-hover > selected variant
    return cardGallery.getAttribute('data-active-variant-id') ||
           cardGallery.getAttribute('data-forced-variant-id') ||
           cardGallery.getAttribute('data-variant-id-for-hover') ||
           this.getSelectedVariantId();
  }

  /**
   * Checks whether the collection page is actually filtered by variant options.
   * `data-active-variant-id` also exists on unfiltered Shop all cards for their
   * default variant, so it cannot be used by itself as a filter signal.
   * @returns {boolean}
   */
  #hasActiveVariantFilter() {
    const params = new URLSearchParams(window.location.search);

    return Array.from(params.keys()).some((key) => key.startsWith('filter.v.option.'));
  }

  /**
   * Fetches hover image URL from variant metafield via section rendering
   * @returns {Promise<string|null>}
   */
  async #fetchHoverImage() {
    const cardGallery = this.refs.cardGallery;
    if (!cardGallery) return null;
    
    const productHandle = cardGallery.getAttribute('data-product-handle');
    const variantId = this.#getCurrentVariantId();
    
    if (!productHandle || !variantId) return null;
    
    // Check cache first
    if (this.#hoverImageCache.has(variantId)) {
      return this.#hoverImageCache.get(variantId) || null;
    }
    
    try {
      const fetchUrl = `/products/${productHandle}?variant=${variantId}&section_id=variant-hover-image`;

      const response = await fetch(fetchUrl);
      if (!response.ok) {
        return null;
      }

      const html = await response.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const el = doc.querySelector('[data-hover-image-url]');

      if (el) {
        const hoverUrl = el.getAttribute('data-hover-image-url');

        if (hoverUrl && hoverUrl.trim()) {
          this.#hoverImageCache.set(variantId, hoverUrl);
          return hoverUrl;
        }
      }
      
      // Cache empty result to avoid re-fetching
      this.#hoverImageCache.set(variantId, '');
    } catch (_e) {
      // Ignore hover-image fetch failures; the regular gallery image remains.
    }
    return null;
  }

  /**
   * Gets the slide that is currently painting the card.
   * @returns {HTMLElement | null}
   */
  #getCurrentSlide() {
    const { slideshow } = this.refs;
    if (!slideshow) return null;

    return (
      slideshow.slides?.[slideshow.current] ||
      slideshow.refs.slides?.find((slide) => slide.getAttribute('aria-hidden') === 'false' && !slide.hasAttribute('hidden')) ||
      slideshow.refs.slides?.find((slide) => !slide.hasAttribute('hidden')) ||
      null
    );
  }

  /**
   * Finds the image to reveal on hover by reading the card's own slides directly,
   * rather than driving the slideshow (which is unreliable when the card has
   * hidden variant slides, e.g. on the unfiltered "Shop all" grid).
   *
   * @param {object} [options]
   * @param {boolean} [options.allowVariantImages=true] - When false, variant
   *   images are skipped. We disable them on filtered cards so hover never jumps
   *   to a different glaze's photo; on unfiltered cards the product's second
   *   media (often a variant image) is exactly what should be revealed.
   * @returns {HTMLImageElement | null}
   */
  #getNextMediaImage({ allowVariantImages = true } = {}) {
    const { slideshow } = this.refs;
    const currentSlide = this.#getCurrentSlide();

    if (!slideshow || !currentSlide) return null;

    const currentImage = currentSlide.querySelector('img');
    const currentSrc = currentImage instanceof HTMLImageElement ? currentImage.currentSrc || currentImage.src : '';
    const slides = slideshow.refs.slides || [];
    const currentIndex = slides.indexOf(currentSlide);
    // Walk forwards from the current slide and wrap, so we reveal the natural
    // "second image" in DOM order. Hidden slides are intentionally allowed:
    // when a product's only extra media are variant images, the theme hides
    // them on the grid, but they are still the image we want to flash on hover.
    const orderedSlides =
      currentIndex === -1 ? slides : slides.slice(currentIndex + 1).concat(slides.slice(0, currentIndex));

    const candidate = orderedSlides.find((slide) => {
      if (slide === currentSlide) return false;
      if (!allowVariantImages && slide.hasAttribute('variant-image')) return false;

      const image = slide.querySelector('img');
      if (!(image instanceof HTMLImageElement)) return false;

      const imageSrc = image.currentSrc || image.src;
      return Boolean(imageSrc) && imageSrc !== currentSrc;
    });

    return candidate?.querySelector('img') || null;
  }

  /**
   * Swaps the visible card image without moving the slideshow.
   * @param {string | HTMLImageElement} source
   * @returns {boolean}
   */
  #swapCurrentImage(source) {
    const currentSlide = this.#getCurrentSlide();
    const img = currentSlide?.querySelector('img');

    if (!(img instanceof HTMLImageElement)) return false;

    if (!img.dataset.originalSrc) {
      img.dataset.originalSrc = img.src;
      img.dataset.originalSrcset = img.srcset || '';
    }

    if (typeof source === 'string') {
      img.src = source;
      img.srcset = '';
      return true;
    }

    img.alt = source.alt;
    img.sizes = source.sizes;
    img.srcset = source.srcset;
    img.src = source.currentSrc || source.src;
    return true;
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  async previewImage(event) {
    if (event.pointerType !== 'mouse') return;
    const hoverToken = ++this.#hoverPreviewToken;

    const cardGallery = this.refs.cardGallery;
    const { slideshow } = this.refs;

    if (!slideshow || !cardGallery) return;

    // Skip hover behavior in informative mode
    if (cardGallery.hasAttribute('data-informative-mode')) return;

    // Get the current variant being displayed
    const currentVariantId = this.#getCurrentVariantId();
    // Get the variant that the HTML's hover URL was rendered for
    const htmlVariantId = cardGallery.getAttribute('data-variant-id-for-hover');
    // Get the existing hover URL from HTML
    const htmlHoverUrl = cardGallery.getAttribute('data-hover-image-url');
    
    let hoverImageUrl = null;
    
    // If HTML has a hover URL AND it's for the current variant, use it
    if (htmlHoverUrl && htmlHoverUrl.trim() && currentVariantId === htmlVariantId) {
      hoverImageUrl = htmlHoverUrl;
    } else {
      // Otherwise, check cache or fetch for the current variant
      hoverImageUrl = currentVariantId ? this.#hoverImageCache.get(currentVariantId) : null;
      // If cached value is empty string, that means we already checked and there's no hover image
      if (hoverImageUrl === undefined) {
        hoverImageUrl = await this.#fetchHoverImage();
        if (
          hoverToken !== this.#hoverPreviewToken ||
          currentVariantId !== this.#getCurrentVariantId() ||
          !cardGallery.matches(':hover')
        ) {
          return;
        }
      } else if (hoverImageUrl === '') {
        // Explicitly set to null if we have an empty cached result
        hoverImageUrl = null;
      }
    }
    
    // Only swap image if we have a valid hover image URL — wait until the
    // image is in browser cache so the first hover paints immediately.
    if (hoverImageUrl && hoverImageUrl.trim() && hoverImageUrl !== '') {
      const ready = await this.#preloadHoverImageUrl(hoverImageUrl);
      if (
        hoverToken !== this.#hoverPreviewToken ||
        currentVariantId !== this.#getCurrentVariantId() ||
        !cardGallery.matches(':hover')
      ) {
        return;
      }
      if (ready && this.#swapCurrentImage(hoverImageUrl)) {
        this.resetVariant.cancel();
        return;
      }
    }

    // After a combined-swatch selection, still allow the metafield hover above,
    // but don't fall through to slideshow next/prev (that fights the swatch image).
    if (cardGallery.hasAttribute('data-combined-swatch-selected')) return;

    const hasFilteredVariantState =
      this.#hasActiveVariantFilter() ||
      cardGallery.hasAttribute('data-forced-variant-id') ||
      cardGallery.hasAttribute('data-forced-glaze-locked') ||
      this.#getForcedVariantId();

    // Reveal the second image with a direct image swap instead of moving the
    // slideshow. This is reliable in every state, including the unfiltered grid
    // where the product's second media is a hidden variant image. On filtered
    // cards we forbid variant images so hover never shows a different glaze.
    const fallbackHoverImage = this.#getNextMediaImage({ allowVariantImages: !hasFilteredVariantState });
    if (fallbackHoverImage && this.#swapCurrentImage(fallbackHoverImage)) {
      this.resetVariant.cancel();
      return;
    }

    if (hasFilteredVariantState) return;

    // Last resort for real carousels where slides aren't pre-rendered.
    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;
    this.#hoverPreviewToken += 1;

    const cardGallery = this.refs.cardGallery;
    const { slideshow } = this.refs;

    if (!slideshow || !cardGallery) return;

    // Skip hover behavior in informative mode
    if (cardGallery.hasAttribute('data-informative-mode')) return;

    // Restore any hover image swap, including URLs fetched after filter sync.
    const currentSlide = this.#getCurrentSlide();
    if (currentSlide) {
      const img = currentSlide.querySelector('img');
      if (img instanceof HTMLImageElement && img.dataset.originalSrc) {
        img.src = img.dataset.originalSrc;
        img.srcset = img.dataset.originalSrcset || '';
        delete img.dataset.originalSrc;
        delete img.dataset.originalSrcset;
        return;
      }
    }

    // After a combined-swatch selection, keep the swatch image (don't slideshow-reset).
    if (cardGallery.hasAttribute('data-combined-swatch-selected')) return;

    // Disable hover image reset when a glaze/clay filter is active (only for default behavior)
    if (this.#hasActiveVariantFilter() ||
        cardGallery?.hasAttribute('data-forced-variant-id') ||
        cardGallery?.hasAttribute('data-forced-glaze-locked') ||
        this.#getForcedVariantId()) {
      return;
    }

    if (!this.variantPicker) {
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    // Disable variant reset for product cards in recommendations section
    if (this.hasAttribute('data-in-recommendations')) {
      return;
    }

    const { slideshow } = this.refs;
    if (!slideshow) return;

    // PRIORITY 1: If we have a forced glaze, ALWAYS use that variant's media ID
    // This takes absolute priority - never use the picker's selectedOption when forced variant exists
    const forcedVariantId = this.#getForcedVariantId();
    if (forcedVariantId) {
      const forcedMediaId = this.#getForcedVariantMediaId();
      if (forcedMediaId) {
        slideshow.select({ id: forcedMediaId }, undefined, { animate: false });
        return;
      }
      // If we have forced variant ID but no media ID, try to find it from the input
      const forcedInput = this.querySelector(`input[data-variant-id="${forcedVariantId}"]`);
      if (forcedInput) {
        const mediaId = forcedInput.dataset.optionMediaId;
        if (mediaId) {
          slideshow.select({ id: mediaId }, undefined, { animate: false });
          return;
        }
      }
    }

    // PRIORITY 2: If we have a selected variant, use its image
    // Only check this if NO forced variant is set
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // PRIORITY 3: Use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // FALLBACK: No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = this.closest('li');
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set('page', parent.dataset.page);
    }

    if (!window.Shopify.designMode) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 *
 * @typedef {object} SwatchesRefs
 * @property {HTMLElement} overflowList
 *
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));

    // Listen for clicks on swatch inputs/labels to navigate to variant page
    this.addEventListener('click', this.#handleSwatchClick.bind(this));

    // Setup carousel for combined swatches
    this.#setupCombinedSwatchesCarousel();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setupCombinedSwatchesCarousel();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.querySelectorAll('[data-combined-swatches-carousel]').forEach((carousel) => {
      const teardown = /** @type {any} */ (carousel).__swatchTeardown;
      if (typeof teardown === 'function') teardown();
    });
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Handles clicks on swatch inputs/labels to navigate to variant page.
   * @param {Event} event - The click event.
   */
  #handleSwatchClick(event) {
    // On product cards, combined swatches cycle in-place, never navigate away.
    const combinedInput = event.target.closest('input[data-combined-swatch="true"]');
    const combinedLabel = event.target.closest('label.combined-swatch');
    if ((combinedInput || combinedLabel) && this.closest('product-card')) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // Only handle clicks on swatch inputs or their labels
    const swatchInput = event.target.closest('input[type="radio"][name*="-swatch"]');
    const swatchLabel = event.target.closest('label.variant-option__button-label--has-swatch');
    
    if (!swatchInput && !swatchLabel) return;
    
    // Get the actual input element (either the clicked input or the one inside the label)
    const input = swatchInput || swatchLabel?.querySelector('input[type="radio"]');
    if (!input) return;
    
    // Get variant ID from the input
    const variantId = input.dataset.variantId || input.dataset.firstAvailableOrFirstVariantId;
    if (!variantId) return;
    
    // Get product URL
    const productUrl = this.dataset.productUrl?.split('?')[0];
    if (!productUrl) return;
    
    // Build variant URL
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('variant', variantId);
    
    // Navigate to the variant page
    window.location.href = url.toString();
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a combined swatch input
    const combinedSwatchInput =
      event.target instanceof HTMLInputElement && event.target.dataset.combinedSwatch === 'true'
        ? event.target
        : null;

    if (combinedSwatchInput) {
      event.preventDefault();
      event.stopPropagation();
      this.updateSelectedOption(combinedSwatchInput);
      this.dispatchEvent(new VariantSelectedEvent({ id: combinedSwatchInput.dataset.optionValueId ?? '' }));
      this.#handleCombinedSwatchSelection(combinedSwatchInput);
      return;
    }

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }

  /**
   * Handles variant changes initiated by the combined swatch UI.
   * @param {HTMLInputElement} input
   */
  #handleCombinedSwatchSelection(input) {
    const variantId = input.dataset.variantId;
    const productUrl = this.dataset.productUrl?.split('?')[0];

    if (!variantId || !productUrl) return;

    // Check if we're in a product card - if so, just update URL and preview, don't fetch
    const isInProductCard = this.closest('product-card') !== null;
    
    if (isInProductCard) {
      // For product cards, just update the URL and let the card handle preview
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', variantId);
      
      if (this.parentProductCard instanceof ProductCard) {
        const nextHref = url.toString();
        this.parentProductCard.refs.productCardLink.href = nextHref;
        // Image / title clicks use these nested anchors — keep them in sync
        // with the filtered / swatch-selected variant or the PDP opens wrong.
        const { cardGalleryLink, productTitleLink } = this.parentProductCard.refs;
        if (cardGalleryLink instanceof HTMLAnchorElement) {
          cardGalleryLink.href = nextHref;
        }
        if (productTitleLink instanceof HTMLAnchorElement) {
          productTitleLink.href = nextHref;
        }

        this.parentProductCard.selectCombinedSwatch(input);
      }
      
      // Note: We don't dispatch VariantUpdateEvent here for product cards
      // The card will handle price/image updates through the URL change
    } else {
      // For product pages, fetch the updated section
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', variantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      this.pendingVariantId = variantId;
      this.fetchUpdatedSection(url.href);
    }
  }

  /**
   * Sets up the carousel functionality for combined swatches.
   */
  #setupCombinedSwatchesCarousel() {
    const carousels = this.querySelectorAll('[data-combined-swatches-carousel]');
    if (!carousels.length) return;

    carousels.forEach((carousel) => {
      if (!(carousel instanceof HTMLElement)) return;

      const viewport = carousel.querySelector('[data-combined-swatches-viewport]');
      const track = carousel.querySelector('[data-combined-swatches-track]');
      const list = carousel.querySelector('[data-combined-swatches-list]');

      if (!(viewport instanceof HTMLElement) || !(track instanceof HTMLElement) || !list) {
        return;
      }

      // This runs on first connect AND again after every filter/sort change (via
      // updatedCallback, because facets re-render the grid with morph). Morph
      // patches the existing elements in place, so listeners/observers from a
      // previous init survive, but with stale closures pointing at clone nodes
      // morph has since removed. If we don't tear the old instance down first,
      // the duplicate handlers fight over the track transform and the carousel
      // collapses (e.g. shows only a single swatch after filtering).
      const carouselEl = /** @type {any} */ (carousel);
      if (typeof carouselEl.__swatchTeardown === 'function') {
        carouselEl.__swatchTeardown();
      }

      // Always start from the clean, server-rendered swatch set: drop leftover
      // clones and reset any inline track styles a previous instance applied.
      list.querySelectorAll('.combined-swatches-item--clone').forEach((clone) => clone.remove());
      track.style.transform = '';
      track.style.transition = '';
      track.style.paddingInline = '';
      track.style.justifyContent = '';
      track.style.width = '';

      const controller = new AbortController();
      const { signal } = controller;
      /** @type {ResizeObserver | null} */
      let resizeObserver = null;
      /** @type {IntersectionObserver | null} */
      let visibilityObserver = null;
      const hostCard = this.closest('product-card') || this;

      carouselEl.__swatchTeardown = () => {
        controller.abort();
        resizeObserver?.disconnect();
        visibilityObserver?.disconnect();
        carouselEl.__swatchTeardown = null;
      };

      const chevronLeft = carousel.querySelector('[data-combined-swatches-chevron-left]');
      const chevronRight = carousel.querySelector('[data-combined-swatches-chevron-right]');

      const items = Array.from(list.querySelectorAll('.combined-swatches-item'));
      const totalItems = items.length;
      // Cycle for any product with 2+ swatches; a single swatch is static.
      const isLoop = totalItems >= 2;

      // Determine the initially-selected real index BEFORE adding clones.
      // The server-rendered checked radio can be the product's first variant
      // (often white), while the gallery may have already chosen a product-
      // specific default via data-active-variant-id.
      let activeIndex = 0;
      const cardGallery = hostCard.querySelector('[ref="cardGallery"]');
      const activeVariantId =
        cardGallery instanceof HTMLElement
          ? cardGallery.getAttribute('data-forced-variant-id') || cardGallery.getAttribute('data-active-variant-id')
          : null;
      const activeVariantInput = activeVariantId
        ? items
            .map((item) => item.querySelector(`.combined-swatch-input[data-variant-id="${activeVariantId}"]`))
            .find((input) => input instanceof HTMLInputElement)
        : null;
      const checkedInput = activeVariantInput || list.querySelector('.combined-swatch-input:checked');
      if (checkedInput instanceof HTMLInputElement) {
        const idx = items.findIndex((item) => item.contains(checkedInput));
        if (idx !== -1) activeIndex = idx;
      }

      // For infinite looping, clone the FULL set of swatches on both sides of the
      // real set. This guarantees the left and right neighbour slots are always
      // filled, even for 2 or 3 variants, so the active one stays centred and the
      // loop is seamless in both directions (small sets simply repeat).
      const makeClone = (source) => {
        const clone = source.cloneNode(true);
        clone.classList.add('combined-swatches-item--clone');
        clone.querySelectorAll('input').forEach((input) => {
          // Clones must NEVER be considered the selected variant. cloneNode copies
          // the checked state/attribute, and because we strip the radio `name` the
          // clone can never be unchecked by selecting a real swatch. A leftover
          // checked clone (it sits before the real swatches in the DOM) would win
          // `fieldset input:checked`, so resetVariant would snap the main image back
          // to the original colour after every selection. Clear it on both the
          // property and the attribute.
          input.checked = false;
          input.removeAttribute('checked');
          input.disabled = true;
          input.removeAttribute('name');
        });
        return clone;
      };

      if (isLoop) {
        const leading = document.createDocumentFragment();
        const trailing = document.createDocumentFragment();
        items.forEach((item) => leading.appendChild(makeClone(item)));
        items.forEach((item) => trailing.appendChild(makeClone(item)));
        list.insertBefore(leading, items[0]);
        list.appendChild(trailing);
      }

      // All slots in DOM order, including clones. With a full leading clone set,
      // a real item `i` lives at slot `totalItems + i`.
      const slots = Array.from(list.querySelectorAll('.combined-swatches-item'));
      const slotFor = (realIndex) => (isLoop ? totalItems + realIndex : realIndex);
      const wrap = (n) => ((n % totalItems) + totalItems) % totalItems;

      let isAnimating = false;
      let isSyncingInput = false;

      const setOverflow = () => {
        carousel.dataset.hasOverflow = isLoop ? 'true' : 'false';
        [chevronLeft, chevronRight].forEach((btn) => {
          if (btn instanceof HTMLElement) btn.setAttribute('aria-hidden', isLoop ? 'false' : 'true');
        });
      };

      const preloadInput = (input) => {
        if (this.parentProductCard instanceof ProductCard && input instanceof HTMLInputElement) {
          this.parentProductCard.preloadCombinedSwatchImages([input]);
        }
      };

      const syncInput = (realIndex) => {
        const input = items[realIndex]?.querySelector('.combined-swatch-input');
        if (input instanceof HTMLInputElement && !input.checked) {
          input.checked = true;
          isSyncingInput = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
          isSyncingInput = false;
        }
      };

      // Position the track so the slot at `slotIndex` sits centred in the viewport.
      const positionSlot = (slotIndex, animate) => {
        void viewport.offsetWidth;
        void track.offsetWidth;

        const cs = getComputedStyle(carousel);
        const slotSize =
          parseFloat(cs.getPropertyValue('--combined-card-swatch-size-active').trim()) || 34;
        const gapSize =
          parseFloat(cs.getPropertyValue('--combined-card-swatch-gap').trim()) || 5;

        const vpStyles = getComputedStyle(viewport);
        const vpPadL = parseFloat(vpStyles.paddingLeft) || 0;
        const vpPadR = parseFloat(vpStyles.paddingRight) || 0;
        const innerWidth = viewport.clientWidth - vpPadL - vpPadR;

        // Bail if the carousel isn't laid out yet (off-screen card with
        // content-visibility, hidden/hover-reveal state, etc.). Measuring a
        // 0-width viewport would push the whole track off-screen and the
        // swatches would appear to "not load". We still mark the active slot
        // and let a later settle() (on visibility/hover/resize) position it.
        if (innerWidth <= 0) {
          slots.forEach((slot, i) =>
            slot.classList.toggle('combined-swatches-item--active', i === slotIndex)
          );
          return;
        }

        const step = slotSize + gapSize;
        const trackPad = (innerWidth - slotSize) / 2;
        const slotCenter = trackPad + slotIndex * step + slotSize / 2;
        const target = Math.max(0, slotCenter - innerWidth / 2);

        if (!animate) track.style.transition = 'none';
        track.style.transform = `translateX(-${target}px)`;
        carousel.dataset.scrollPosition = String(target);
        if (!animate) {
          void track.offsetWidth;
          track.style.transition = '';
        }

        slots.forEach((slot, i) =>
          slot.classList.toggle('combined-swatches-item--active', i === slotIndex)
        );
      };

      // Single swatch only: no cycling, just show it centred with no transform.
      const placeStatic = () => {
        track.style.transition = 'none';
        track.style.paddingInline = '0';
        track.style.justifyContent = 'center';
        track.style.width = '100%';
        track.style.transform = 'translateX(0)';
        void track.offsetWidth;
        track.style.transition = '';
        items.forEach((item, i) =>
          item.classList.toggle('combined-swatches-item--active', i === activeIndex)
        );
      };

      // Jump (no animation) back to the canonical slot for the current real index.
      const settle = () => {
        if (!isLoop) {
          placeStatic();
          return;
        }
        positionSlot(slotFor(activeIndex), false);
      };

      // Animate to an arbitrary slot, then on completion settle on the real index.
      const animateToSlot = (slotIndex, finalRealIndex) => {
        if (isAnimating) return;
        isAnimating = true;
        syncInput(wrap(finalRealIndex));

        const onEnd = (event) => {
          if (event.target !== track || event.propertyName !== 'transform') return;
          track.removeEventListener('transitionend', onEnd);
          activeIndex = wrap(finalRealIndex);
          settle();
          isAnimating = false;
        };
        track.addEventListener('transitionend', onEnd, { signal });

        positionSlot(slotIndex, true);
      };

      const goNext = () => {
        if (isAnimating || !isLoop) return;
        if (activeIndex === totalItems - 1) {
          // Slide onto the first trailing clone (shows item 0), then snap to real 0.
          animateToSlot(2 * totalItems, 0);
        } else {
          activeIndex += 1;
          syncInput(activeIndex);
          positionSlot(slotFor(activeIndex), true);
        }
      };

      const goPrev = () => {
        if (isAnimating || !isLoop) return;
        if (activeIndex === 0) {
          // Slide onto the last leading clone (shows last item), then snap to real last.
          animateToSlot(totalItems - 1, totalItems - 1);
        } else {
          activeIndex -= 1;
          syncInput(activeIndex);
          positionSlot(slotFor(activeIndex), true);
        }
      };

      const selectRealIndex = (realIndex) => {
        if (isAnimating) return;
        const target = wrap(realIndex);
        if (target === activeIndex) return;

        if (!isLoop) {
          activeIndex = target;
          syncInput(activeIndex);
          placeStatic();
          return;
        }

        activeIndex = target;
        syncInput(activeIndex);
        positionSlot(slotFor(activeIndex), true);
      };

      list.addEventListener(
        'click',
        (event) => {
          const target = event.target;
          if (!(target instanceof Element)) return;
          const label = target.closest('label.combined-swatch');
          if (!label) return;

          const item = label.closest('.combined-swatches-item');
          if (!item || item.classList.contains('combined-swatches-item--clone')) return;

          event.preventDefault();
          event.stopPropagation();

          const input = label.querySelector('.combined-swatch-input:not([disabled])');
          if (!(input instanceof HTMLInputElement)) return;

          const realIndex = items.findIndex((i) => i.contains(input));
          if (realIndex === -1) return;

          selectRealIndex(realIndex);
        },
        { signal }
      );

      list.addEventListener(
        'change',
        (event) => {
          if (isSyncingInput) return;

          const input = event.target;
          if (!(input instanceof HTMLInputElement) || input.dataset.combinedSwatch !== 'true') return;

          const item = input.closest('.combined-swatches-item');
          if (!item || item.classList.contains('combined-swatches-item--clone')) return;

          const realIndex = items.findIndex((i) => i.contains(input));
          if (realIndex === -1) return;

          activeIndex = wrap(realIndex);
          if (!isLoop) {
            placeStatic();
            return;
          }

          positionSlot(slotFor(activeIndex), false);
        },
        { signal }
      );

      list.addEventListener(
        'pointerover',
        (event) => {
          const target = event.target;
          if (!(target instanceof Element)) return;

          const input = target
            .closest('label.combined-swatch')
            ?.querySelector('.combined-swatch-input:not([disabled])');
          preloadInput(input);
        },
        { signal }
      );

      chevronRight?.addEventListener('click', goNext, { signal });
      chevronLeft?.addEventListener('click', goPrev, { signal });

      const ensureSettled = () => {
        if (!isAnimating) settle();
      };

      requestAnimationFrame(() => {
        if (signal.aborted) return;
        setOverflow();
        syncInput(activeIndex);
        settle();
        setTimeout(() => {
          if (!signal.aborted) settle();
        }, 120);
      });

      resizeObserver = new ResizeObserver(() => ensureSettled());
      resizeObserver.observe(viewport);
      resizeObserver.observe(list);

      // Re-settle the moment the carousel actually becomes visible/measurable.
      // Covers off-screen cards (content-visibility), lazy layout, and the
      // desktop hover-reveal where width is only correct once shown.
      if ('IntersectionObserver' in window) {
        visibilityObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) ensureSettled();
          });
        });
        visibilityObserver.observe(viewport);
      }

      hostCard.addEventListener(
        'pointerenter',
        () => {
          ensureSettled();
        },
        { signal }
      );
    });
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
